import {
  ArrowUpDown,
  Bell,
  Calendar,
  CalendarOff,
  ChevronDown,
  CircleDot,
  Flag,
  Globe,
  Handshake,
  ShieldAlert,
  SlidersHorizontal,
  Star,
  Users,
} from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { UserLabel } from '@/components/ui/user-label'
import { cn } from '@/lib/utils'
import { useSalesLeadTags } from '@/models/salesLeadTag'
import {
  BuyerType,
  buyerTypeLabels,
  LeadSource,
  SafetyRating,
  safetyRatingLabels,
  type SalesLeadStatus,
  salesLeadStatusLabels,
} from '../../../../../models/salesLead'
import { type StaffUser, staffUserName } from '../../../../../models/user'
import { EligibilitySelect } from './EligibilitySelect'
import { StateFilterSelect } from './StateFilterSelect'
import { StatusChip } from './StatusChip'
import { TagSelect } from './TagSelect'

type UpdateParams = (updates: Record<string, string | undefined>) => void

interface FilterControlProps {
  value: string
  onChange: (value: string) => void
  className?: string
}

// Canonical status ordering, shared by the options list and the trigger chips.
const STATUS_ORDER = Object.keys(salesLeadStatusLabels) as SalesLeadStatus[]

// Multi-select status filter. `value` is a comma-separated list of statuses so
// it round-trips cleanly through the URL `status` param; `onChange` emits the
// same comma-separated form ('' when nothing is selected).
export function StatusSelect({
  value,
  onChange,
  className,
  disabled,
}: FilterControlProps & { disabled?: boolean }) {
  const selected = disabled ? [] : STATUS_ORDER.filter((s) => value.split(',').includes(s))
  const selectedSet = new Set(selected)

  function toggle(status: SalesLeadStatus) {
    const next = new Set(selectedSet)
    if (next.has(status)) next.delete(status)
    else next.add(status)
    onChange(STATUS_ORDER.filter((s) => next.has(s)).join(','))
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outlineThin"
          disabled={disabled}
          className={cn('justify-start gap-2 font-normal', className)}
        >
          <CircleDot className="text-muted-foreground size-4 shrink-0" />
          <span className="flex min-w-0 flex-1 items-center gap-1 overflow-hidden">
            {selected.length === 0 ? (
              <span className="text-muted-foreground">All Statuses</span>
            ) : (
              <>
                <StatusChip status={selected[0]} className="min-w-0 truncate" />
                {selected.length > 1 && (
                  <span className="text-muted-foreground shrink-0 text-xs">+{selected.length - 1}</span>
                )}
              </>
            )}
          </span>
          <ChevronDown className="text-muted-foreground size-4 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-56 p-1">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => onChange('')}
          disabled={selected.length === 0}
          className="text-muted-foreground w-full justify-start font-normal"
        >
          All Statuses
        </Button>
        <div className="bg-border -mx-1 my-1 h-px" />
        {STATUS_ORDER.map((status) => (
          <label
            key={status}
            className="hover:bg-accent flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5"
          >
            <Checkbox checked={selectedSet.has(status)} onCheckedChange={() => toggle(status)} />
            <StatusChip status={status} />
          </label>
        ))}
      </PopoverContent>
    </Popover>
  )
}

interface RepSelectProps extends FilterControlProps {
  staffUsers: StaffUser[]
}

export function RepSelect({ value, staffUsers, onChange, className }: RepSelectProps) {
  return (
    <Select value={value || 'all'} onValueChange={(v) => onChange(v === 'all' ? '' : v)}>
      <SelectTrigger className={className}>
        <Users className="text-muted-foreground mr-2 size-4" />
        <SelectValue placeholder="Assigned to" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="all">All Reps</SelectItem>
        <SelectItem value="me">My Leads</SelectItem>
        <SelectItem value="unassigned">Unassigned</SelectItem>
        <SelectSeparator />
        {staffUsers.map((u) => (
          <SelectItem key={u.id} value={u.id}>
            <UserLabel name={staffUserName(u)} src={u.avatar} />
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

// Inbound means the carrier entered their own DOT on the website.
export function SourceSelect({ value, onChange, className }: FilterControlProps) {
  return (
    <Select value={value || 'all'} onValueChange={(v) => onChange(v === 'all' ? '' : v)}>
      <SelectTrigger className={className}>
        <Globe className="text-muted-foreground mr-2 size-4" />
        <SelectValue placeholder="Source" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="all">Any source</SelectItem>
        <SelectItem value={LeadSource.INBOUND}>Inbound</SelectItem>
        <SelectItem value={LeadSource.OUTBOUND}>Outbound</SelectItem>
      </SelectContent>
    </Select>
  )
}

// Shared by both policy-expiration selects so the windows stay symmetric.
const EXPIRING_DAY_OPTIONS = [1, 2, 7, 10, 14, 21, 30, 60, 90]

function ExpiringDayItems() {
  return (
    <>
      {EXPIRING_DAY_OPTIONS.map((days) => (
        <SelectItem key={days} value={String(days)}>
          {days === 1 ? 'Next day' : `Next ${days} days`}
        </SelectItem>
      ))}
    </>
  )
}

function ExpiringSelect({ value, onChange, className }: FilterControlProps) {
  return (
    <Select value={value || 'all'} onValueChange={(v) => onChange(v === 'all' ? '' : v)}>
      <SelectTrigger className={className}>
        <Calendar className="text-muted-foreground mr-2 size-4" />
        <SelectValue placeholder="Policy expiring" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="all">Any time</SelectItem>
        <ExpiringDayItems />
      </SelectContent>
    </Select>
  )
}

// The inverse window: keeps only leads whose policy expires AFTER the chosen
// number of days. Pairs with ExpiringSelect to bracket a renewal window.
function NotExpiringSelect({ value, onChange, className }: FilterControlProps) {
  return (
    <Select value={value || 'all'} onValueChange={(v) => onChange(v === 'all' ? '' : v)}>
      <SelectTrigger className={className}>
        <CalendarOff className="text-muted-foreground mr-2 size-4" />
        <SelectValue placeholder="Policy not expiring" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="all">Any time</SelectItem>
        <ExpiringDayItems />
      </SelectContent>
    </Select>
  )
}

function MinScoreSelect({ value, onChange, className }: FilterControlProps) {
  return (
    <Select value={value || 'any'} onValueChange={(v) => onChange(v === 'any' ? '' : v)}>
      <SelectTrigger className={className}>
        <Star className="text-muted-foreground mr-2 size-4" />
        <SelectValue placeholder="Min score" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="any">Any score</SelectItem>
        <SelectItem value="8">Score ≥ 8</SelectItem>
        <SelectItem value="6">Score ≥ 6</SelectItem>
        <SelectItem value="4">Score ≥ 4</SelectItem>
      </SelectContent>
    </Select>
  )
}

function SafetyRatingSelect({ value, onChange, className }: FilterControlProps) {
  return (
    <Select value={value || 'any'} onValueChange={(v) => onChange(v === 'any' ? '' : v)}>
      <SelectTrigger className={className}>
        <ShieldAlert className="text-muted-foreground mr-2 size-4" />
        <SelectValue placeholder="Safety" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="any">Any safety</SelectItem>
        {Object.values(SafetyRating).map((rating) => (
          <SelectItem key={rating} value={rating}>
            {safetyRatingLabels[rating]}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

function BuyerTypeSelect({ value, onChange, className }: FilterControlProps) {
  return (
    <Select value={value || 'any'} onValueChange={(v) => onChange(v === 'any' ? '' : v)}>
      <SelectTrigger className={className}>
        <Handshake className="text-muted-foreground mr-2 size-4" />
        <SelectValue placeholder="Buyer type" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="any">Any buyer type</SelectItem>
        {Object.values(BuyerType).map((buyerType) => (
          <SelectItem key={buyerType} value={buyerType}>
            {buyerTypeLabels[buyerType]}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

const ORDERING_OPTIONS = [
  { value: 'default', label: 'Default order' },
  { value: 'policy_expiration_date', label: 'Expires · soonest first' },
  { value: '-policy_expiration_date', label: 'Expires · latest first' },
  { value: '-power_units', label: 'Units · most first' },
  { value: 'power_units', label: 'Units · fewest first' },
  { value: '-conversion_score', label: 'Score · high to low' },
  { value: 'conversion_score', label: 'Score · low to high' },
  { value: '-last_activity_at', label: 'Last activity · newest' },
  { value: 'last_activity_at', label: 'Last activity · oldest' },
]

export function OrderingSelect({ value, onChange, className }: FilterControlProps) {
  return (
    <Select value={value || 'default'} onValueChange={(v) => onChange(v === 'default' ? '' : v)}>
      <SelectTrigger className={className}>
        <ArrowUpDown className="text-muted-foreground mr-2 size-4" />
        <SelectValue placeholder="Sort by" />
      </SelectTrigger>
      <SelectContent>
        {ORDERING_OPTIONS.map((o) => (
          <SelectItem key={o.value} value={o.value}>
            {o.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

export interface MoreFiltersState {
  statusFilter: string
  expiringDays: string
  notExpiringDays: string
  assignedRep: string
  stateFilter: string
  minScore: string
  safetyRating: string
  buyerType: string
  minUnits: string
  maxUnits: string
  unreadOnly: boolean
  followupOnly: boolean
  // Comma-separated SalesLeadTag ids to include (leads having ANY of them).
  tagIds: string
  // Comma-separated SalesLeadTag ids to exclude (leads having ANY of them).
  excludeTagIds: string
  // Comma-separated `<carrier>:<eligible|not_eligible>` picks (leads holding ANY of them).
  eligibility: string
}

// Counts only filters that aren't already visible in the bar (Status/Rep show
// their own state there), so the badge surfaces hidden active filters.
export function activeMoreFiltersCount(f: MoreFiltersState): number {
  return (
    [
      f.expiringDays,
      f.notExpiringDays,
      f.stateFilter,
      f.minScore,
      f.safetyRating,
      f.buyerType,
      f.minUnits,
      f.maxUnits,
      f.tagIds,
      f.excludeTagIds,
      f.eligibility,
    ].filter(Boolean).length +
    (f.unreadOnly ? 1 : 0) +
    (f.followupOnly ? 1 : 0)
  )
}

function FilterField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-sm font-medium">{label}</span>
      {children}
    </div>
  )
}

interface MoreFiltersDialogProps {
  filters: MoreFiltersState
  staffUsers: StaffUser[]
  updateParams: UpdateParams
  // Off on the pipeline board, where the columns already are the statuses, so
  // the Status filter is shown disabled (locked to "All Statuses").
  showStatus?: boolean
}

export function MoreFiltersDialog({
  filters,
  staffUsers,
  updateParams,
  showStatus = true,
}: MoreFiltersDialogProps) {
  const { data: allTags = [] } = useSalesLeadTags()
  const count = activeMoreFiltersCount(filters)
  // clearAll() also resets Status/Rep, so the button must enable when those are set too.
  const hasAnyActive = count > 0 || !!filters.statusFilter || !!filters.assignedRep

  const selectedTagIds = filters.tagIds ? filters.tagIds.split(',').filter(Boolean) : []
  const selectedExcludeTagIds = filters.excludeTagIds ? filters.excludeTagIds.split(',').filter(Boolean) : []

  function toggleTag(tagId: string) {
    const next = selectedTagIds.includes(tagId)
      ? selectedTagIds.filter((id) => id !== tagId)
      : [...selectedTagIds, tagId]
    updateParams({ tags: next.join(','), page: undefined })
  }

  function toggleExcludeTag(tagId: string) {
    const next = selectedExcludeTagIds.includes(tagId)
      ? selectedExcludeTagIds.filter((id) => id !== tagId)
      : [...selectedExcludeTagIds, tagId]
    updateParams({ exclude_tags: next.join(','), page: undefined })
  }

  function clearAll() {
    updateParams({
      status: '',
      rep: '',
      expiring: '',
      not_expiring: '',
      min_score: '',
      safety_rating: '',
      buyer_type: '',
      min_units: '',
      max_units: '',
      state: '',
      unread: '',
      followup: '',
      tags: '',
      exclude_tags: '',
      eligibility: '',
      page: undefined,
    })
  }

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="outline" className="justify-between">
          <SlidersHorizontal className="mr-2 size-4" />
          More Filters
          {count > 0 ? (
            <Badge className="size-5 rounded-full p-0 tabular-nums">{count}</Badge>
          ) : (
            <span aria-hidden className="size-4" />
          )}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Filters</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4 py-2">
          <div className="grid grid-cols-2 gap-4">
            <FilterField label="Status">
              <StatusSelect
                value={filters.statusFilter}
                onChange={(v) => updateParams({ status: v, page: undefined })}
                className="w-full"
                disabled={!showStatus}
              />
            </FilterField>
            <FilterField label="Assigned to">
              <RepSelect
                value={filters.assignedRep}
                staffUsers={staffUsers}
                onChange={(v) => updateParams({ rep: v, page: undefined })}
                className="w-full"
              />
            </FilterField>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <FilterField label="Policy expiring">
              <ExpiringSelect
                value={filters.expiringDays}
                onChange={(v) => updateParams({ expiring: v, page: undefined })}
                className="w-full"
              />
            </FilterField>
            <FilterField label="Policy not expiring">
              <NotExpiringSelect
                value={filters.notExpiringDays}
                onChange={(v) => updateParams({ not_expiring: v, page: undefined })}
                className="w-full"
              />
            </FilterField>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <FilterField label="Min score">
              <MinScoreSelect
                value={filters.minScore}
                onChange={(v) => updateParams({ min_score: v, page: undefined })}
                className="w-full"
              />
            </FilterField>
            <FilterField label="Safety">
              <SafetyRatingSelect
                value={filters.safetyRating}
                onChange={(v) => updateParams({ safety_rating: v, page: undefined })}
                className="w-full"
              />
            </FilterField>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <FilterField label="State">
              <StateFilterSelect
                value={filters.stateFilter}
                onChange={(v) => updateParams({ state: v, page: undefined })}
                className="w-full"
              />
            </FilterField>
            <FilterField label="Eligibility">
              <EligibilitySelect
                value={filters.eligibility}
                onChange={(v) => updateParams({ eligibility: v, page: undefined })}
                className="w-full"
              />
            </FilterField>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <FilterField label="Has tags">
              <TagSelect
                tags={allTags}
                selectedIds={selectedTagIds}
                onToggle={toggleTag}
                placeholder="Any tags"
              />
            </FilterField>
            <FilterField label="Doesn't have tags">
              <TagSelect
                tags={allTags}
                selectedIds={selectedExcludeTagIds}
                onToggle={toggleExcludeTag}
                placeholder="Exclude tags"
              />
            </FilterField>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <FilterField label="Buyer type">
              <BuyerTypeSelect
                value={filters.buyerType}
                onChange={(v) => updateParams({ buyer_type: v, page: undefined })}
                className="w-full"
              />
            </FilterField>
            <FilterField label="Fleet size (power units)">
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  min={0}
                  inputMode="numeric"
                  placeholder="Min"
                  value={filters.minUnits}
                  onChange={(e) => updateParams({ min_units: e.target.value, page: undefined })}
                  className="w-full"
                />
                <Input
                  type="number"
                  min={0}
                  inputMode="numeric"
                  placeholder="Max"
                  value={filters.maxUnits}
                  onChange={(e) => updateParams({ max_units: e.target.value, page: undefined })}
                  className="w-full"
                />
              </div>
            </FilterField>
          </div>
          <div className="mt-2 flex gap-2">
            <Button
              variant={filters.unreadOnly ? 'default' : 'outline'}
              onClick={() => updateParams({ unread: filters.unreadOnly ? '' : '1', page: undefined })}
              className="flex-1 justify-start"
            >
              <Bell className="mr-2 size-4" />
              Unread only
            </Button>
            <Button
              variant={filters.followupOnly ? 'default' : 'outline'}
              onClick={() => updateParams({ followup: filters.followupOnly ? '' : '1', page: undefined })}
              className="flex-1 justify-start"
            >
              <Flag className="mr-2 size-4" />
              Follow-up
            </Button>
          </div>
        </div>
        <DialogFooter className="sm:justify-between">
          <Button variant="ghost" onClick={clearAll} disabled={!hasAnyActive}>
            Clear all
          </Button>
          <DialogClose asChild>
            <Button>Done</Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
